import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, FileText } from 'lucide-react';
import type { PortfolioSite } from '../../redux/api/portfolioApi';

interface KnowledgeDropZoneProps {
  isOpen: boolean;
  onClose: () => void;
  sites: PortfolioSite[];
  defaultSiteId?: string;
}

const KnowledgeDropZone: React.FC<KnowledgeDropZoneProps> = ({
  isOpen,
  onClose,
  sites,
  defaultSiteId,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState(defaultSiteId || '');
  const [pipeline, setPipeline] = useState<'intelligence' | 'knowledge'>('intelligence');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...files]);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  }, []);

  const removeFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = useCallback(() => {
    // TODO: Integrate with documentsApi bulk upload in Phase 6
    console.log('Upload:', { files: selectedFiles, siteId: selectedSiteId, pipeline });
    setSelectedFiles([]);
    onClose();
  }, [selectedFiles, selectedSiteId, pipeline, onClose]);

  if (!isOpen) return null;

  return (
    <div className="eg-modal-backdrop" onClick={onClose}>
      <div className="eg-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            + ADD KNOWLEDGE
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--color-surface-hover)] transition-colors"
          >
            <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>

        {/* Drop Zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className="rounded-lg p-8 text-center cursor-pointer transition-colors"
          style={{
            border: `2px dashed ${isDragOver ? '#17c480' : 'var(--color-border)'}`,
            backgroundColor: isDragOver ? 'rgba(23, 196, 128, 0.05)' : 'var(--color-input-bg)',
          }}
        >
          <Upload
            className="w-8 h-8 mx-auto mb-2"
            style={{ color: isDragOver ? '#17c480' : 'var(--color-text-muted)' }}
          />
          <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
            Drop files here or click to browse
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
            PDF, DOCX, XLSX, images, .eml
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.eml"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <div className="mt-3 space-y-1">
            {selectedFiles.map((file, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-2 py-1 rounded"
                style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
              >
                <FileText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                <span className="text-xs truncate flex-1" style={{ color: 'var(--color-text-primary)' }}>
                  {file.name}
                </span>
                <button
                  onClick={() => removeFile(i)}
                  className="text-xs flex-shrink-0 hover:text-[#17c480] transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Site Selector */}
        <div className="mt-4">
          <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--color-text-secondary)' }}>
            Site
          </label>
          <select
            value={selectedSiteId}
            onChange={e => setSelectedSiteId(e.target.value)}
            className="eg-select w-full"
          >
            <option value="">Select a site...</option>
            {sites.map(site => (
              <option key={site.site_id || site.id} value={site.site_id || site.id}>
                {site.site_name || site.name}
              </option>
            ))}
          </select>
        </div>

        {/* Pipeline Toggle */}
        <div className="mt-4">
          <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--color-text-secondary)' }}>
            Pipeline
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => setPipeline('intelligence')}
              className="flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
              style={{
                backgroundColor: pipeline === 'intelligence' ? 'rgba(23, 196, 128, 0.15)' : 'var(--color-input-bg)',
                color: pipeline === 'intelligence' ? '#17c480' : 'var(--color-text-secondary)',
                border: `1px solid ${pipeline === 'intelligence' ? 'rgba(23, 196, 128, 0.4)' : 'var(--color-input-border)'}`,
              }}
            >
              Intelligence
            </button>
            <button
              onClick={() => setPipeline('knowledge')}
              className="flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
              style={{
                backgroundColor: pipeline === 'knowledge' ? 'rgba(23, 196, 128, 0.15)' : 'var(--color-input-bg)',
                color: pipeline === 'knowledge' ? '#17c480' : 'var(--color-text-secondary)',
                border: `1px solid ${pipeline === 'knowledge' ? 'rgba(23, 196, 128, 0.4)' : 'var(--color-input-border)'}`,
              }}
            >
              Knowledge
            </button>
          </div>
        </div>

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || !selectedSiteId}
          className="eg-btn-primary w-full mt-4"
        >
          Upload {selectedFiles.length > 0 ? `(${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''})` : ''}
        </button>
      </div>
    </div>
  );
};

export default KnowledgeDropZone;
